<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig"; 
import Swal from 'sweetalert2';

import { useWindowSize, useCssVar } from "@vueuse/core";

import store from "@/store";
import { getUserData } from "@/auth/utils";
import io from "socket.io-client";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () =>
  import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");
import axiosIns from "@/libs/axios";

export default {
  data() {
    return {
      inactivityTimer: null,
      inactivityTimeout: 20 * 60 * 1000, // 20分鐘
    };
  },
  methods: {
    resetInactivityTimer() {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
      }
      this.inactivityTimer = setTimeout(this.logout, this.inactivityTimeout);
    },
    logout() {
      let timer;
      const that = this;
      Swal.fire({
        title: "是否還在系統中?",
        showCancelButton: false,
        confirmButtonText: "確定",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
        timer: 60000,
        timerProgressBar: true,
        didOpen: () => {
          timer = setInterval(() => {
            const content = Swal.getHtmlContainer();
            if (content) {
              const b = content.querySelector('b');
              if (b) {
                b.textContent = Math.ceil(Swal.getTimerLeft() / 1000);
              }
            }
          }, 100);
        },
        willClose: () => {
          clearInterval(timer);
        }
      }).then((result) => {
        console.log('result', result);
        console.log('Swal.DismissReason', Swal.DismissReason);
        if (result && result.dismiss === Swal.DismissReason.timer) {
          that.performLogout();
        } else if (result && result.isConfirmed) {
          Swal.close();
        }
      });
    },
    performLogout() {
      axiosIns.get("/api/auth/logout");
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
      }
      // 移除事件監聽器
      window.removeEventListener("mousemove", this.resetInactivityTimer);
      window.removeEventListener("keypress", this.resetInactivityTimer);
      localStorage.clear();
      this.$router.replace("/login").then(() => {
        this.$swal({
          text: "由於長時間未操作,您已自動登出。",
          icon: "warning",
          confirmButtonText: "確定",
          customClass: {
            confirmButton: "btn btn-primary",
          },
          showClass: {
            popup: "animate__animated animate__bounceIn",
          },
          buttonsStyling: false,
        });
      });
    },
    // 添加一个方法用于在用户登录后重新添加事件监听器
    addEventListeners() {
      window.addEventListener("mousemove", this.resetInactivityTimer);
      window.addEventListener("keypress", this.resetInactivityTimer);
    },
  },
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },
  mounted() {
    if (getUserData()) {
      store.dispatch("app/fetchUserInfo").then((response) => {
        let userData = response.data.userData;
        localStorage.setItem("userData", JSON.stringify(userData));
        this.$ability.update(userData.ability);

        // 觸發導航選單權限更新
        this.$nextTick(() => {
          if (typeof window !== 'undefined' && window.permissionUpdateService) {
            window.permissionUpdateService.triggerUpdateDelayed('頁面載入', 300);
          } else if (typeof window !== 'undefined' && window.updateNavMenuPermissions) {
            // 向後相容性
            window.updateNavMenuPermissions();
          }
          console.log('✅ 頁面載入後已觸發導航選單權限更新');
        });

        let userInfo = {
          userId: userData.id,
          position: userData.user_type && userData.user_type.type,
          name: userData.name,
        };
        //store.dispatch("app/connectSocket", userInfo)
        this.socket = io(process.env.VUE_APP_SOCKET_URL);
        this.socket.emit("online", userInfo);
        this.socket.on("online-users", (users) => {
          store.commit("app/SET_ONLINE_USERS", users);
        });
      });
    } else {
      // 如果没有获取到用户数据，则需要断开 socket 连接
      // 检查 this.socket 是否已经存在并连接
      if (this.socket && this.socket.connected) {
        this.socket.disconnect();
      }
    }
  },
  created() {
    if (getUserData()) {
      // 初始化計時器
      this.resetInactivityTimer();
      // 添加事件監聽器
      this.addEventListeners();
    }
    // 监听路由变化
    this.$router.beforeEach((to, from, next) => {
      if (getUserData()) {
        this.resetInactivityTimer();
      }
      next();
    });
  },
  beforeDestroy() {
    // 移除事件監聽器
    window.removeEventListener("mousemove", this.resetInactivityTimer);
    window.removeEventListener("keypress", this.resetInactivityTimer);
    // 清除計時器
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    if (this.socket) {
      this.socket.disconnect();
    }
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    return {
      skinClasses,
    };
  },
};
</script>

